import {
	isSound,
	remainingMs,
	SOUND_WIDTH,
	speakerMood,
	speakerSize,
	waveBars,
	type Playback,
} from "@aybuben/client/lesson/speaker.ts";
import { Volume2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useStrings } from "../i18n/index.tsx";
import { COL, FOCUS } from "../theme.ts";
import { useListening } from "./listening/context.tsx";
import { Mascot } from "./Mascot.tsx";
import { FitG, G } from "./primitives.tsx";
import { usePlayback, useSound } from "./sound.tsx";

export interface SpeakerWord {
	t: string;
	asset?: string;
}

interface SpeakerRowProps {
	/** The recording; absent means no speaker icon and no waveform. */
	asset?: string;
	/** Shown instead of the waveform; a word with a recording plays on tap. */
	text?: SpeakerWord[];
	status: "right" | "wrong" | null;
	/** One Armenian word as the card's hero: the glyph font at display size, shrunk to fit the bubble. */
	display?: boolean;
}

/** The Armenian word of a `display` bubble, before it shrinks to fit. */
const DISPLAY_SIZE = 56;

/** One layer of the wave; the lit one lies over the unlit one, clipped to how far the sound has played. */
const Bars = ({ asset, color }: { asset: string; color: string }) => (
	<span className="flex h-full min-w-0 flex-1 items-center gap-[3px]">
		{waveBars(asset).map((h, i) => (
			<span
				key={i}
				data-bar
				className="flex-1 rounded-full"
				style={{ height: `${Math.round(h * 100)}%`, background: color }}
			/>
		))}
	</span>
);

/** The viewport width, kept current: the duduk shrinks below 360 px. */
function useViewportWidth(): number {
	const [w, setW] = useState(() => window.innerWidth);
	useEffect(() => {
		const on = () => setW(window.innerWidth);
		window.addEventListener("resize", on);
		return () => window.removeEventListener("resize", on);
	}, []);
	return w;
}

const clipAt = (progress: number) => `inset(0 ${(1 - progress) * 100}% 0 0)`;

/**
 * The lit wave glides to the end over what is left of the clip. The player
 * reports a few times a second, which drawn as is steps and lags; each report
 * restarts the glide from where the sound really is.
 */
function useGlide(talking: boolean, playback: Playback) {
	const lit = useRef<HTMLSpanElement>(null);
	const glide = useRef<Animation | undefined>(undefined);
	useEffect(() => {
		const el = lit.current;
		if (!el) return;
		glide.current?.cancel();
		if (!talking) {
			el.style.clipPath = clipAt(0);
			return;
		}
		const ms = remainingMs(playback);
		el.style.clipPath = clipAt(ms > 0 ? 1 : playback.progress);
		// The end state stands under the glide, so a test reads it and nothing snaps back after.
		if (ms > 0) {
			glide.current = el.animate(
				[{ clipPath: clipAt(playback.progress) }, { clipPath: clipAt(1) }],
				{
					duration: ms,
					easing: "linear",
				},
			);
			// A cancelled glide rejects `finished`; the next report replaces it, nothing waits on it.
			glide.current?.finished.catch(() => {});
		}
	}, [talking, playback]);
	return lit;
}

/** The duduk with a speech bubble: the prompt, spoken or written. */
export function SpeakerRow({ asset: heard, text: spoken, status, display }: SpeakerRowProps) {
	const t = useStrings();
	// Muted, the bubble is the written prompt alone: no wave, no speaker, no tap to hear.
	const muted = useListening().off !== null;
	const asset = muted ? undefined : heard;
	const text: SpeakerWord[] | undefined = muted
		? spoken?.map(({ t: word }) => ({ t: word }))
		: spoken;
	const sound = useSound();
	const playback = usePlayback();
	const width = useViewportWidth();
	const mine = new Set([asset, ...(text ?? []).map((w) => w.asset)].filter((a) => a !== undefined));
	const talking = playback.asset !== null && mine.has(playback.asset);
	const lit = useGlide(talking, playback);
	const surface = {
		background: COL.card,
		border: `2px solid ${COL.line}`,
		boxShadow: `0 4px 0 ${COL.line}`,
	};
	const hero = display ? text?.[0] : undefined;
	const content = hero ? (
		<>
			<span className="min-w-0 flex-1">
				<FitG size={DISPLAY_SIZE}>{hero.t}</FitG>
			</span>
			{/* the speaker button's width again, so the word centres on the bubble, not beside the button */}
			{asset && <span className="w-8 shrink-0" aria-hidden="true" />}
		</>
	) : text ? (
		<span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
			{text.map((w, i) =>
				w.asset ? (
					<button
						key={i}
						type="button"
						onClick={() => sound.play(w.asset!)} // narrowed by the ternary; the closure loses it
						className={`underline decoration-dotted ${FOCUS}`}
					>
						<G size={30}>{w.t}</G>
					</button>
				) : (
					<span key={i} className="text-lg font-semibold">
						{w.t}
					</span>
				),
			)}
		</span>
	) : asset ? (
		<span className="flex h-9 min-w-0 flex-1 justify-center" aria-hidden="true">
			<span
				className="relative flex h-full"
				style={{ width: isSound(asset) ? `${SOUND_WIDTH * 100}%` : "100%" }}
			>
				<Bars asset={asset} color={COL.line} />
				<span
					ref={lit}
					data-testid="wave-lit"
					className="absolute inset-0 flex"
					style={{ clipPath: clipAt(0) }}
				>
					<Bars asset={asset} color={COL.lapis} />
				</span>
			</span>
		</span>
	) : null;
	const icon = asset ? (
		<Volume2 size={24} style={{ color: COL.lapisDark }} className="shrink-0" />
	) : null;
	return (
		<div className="flex items-end gap-1" data-testid="speaker-row">
			<Mascot state={speakerMood(talking, status)} size={speakerSize(width)} />
			<div className="relative min-w-0 flex-1">
				<span
					aria-hidden="true"
					className="absolute bottom-8 -left-[7px] h-3 w-3 rotate-45"
					style={{
						background: COL.card,
						borderLeft: `2px solid ${COL.line}`,
						borderBottom: `2px solid ${COL.line}`,
					}}
				/>
				{text ? (
					<div
						data-testid="speaker-bubble"
						// A long display word shrinks to fit; the bubble keeps the hero's height so it still reads as one.
						className={`relative flex items-center gap-3 rounded-3xl px-4 py-3 ${display ? "min-h-[90px]" : "min-h-16"}`}
						style={surface}
					>
						{asset && (
							<button
								type="button"
								aria-label={t("lesson.listen")}
								onClick={() => sound.play(asset)}
								className={`shrink-0 rounded-full p-1 ${FOCUS}`}
							>
								{icon}
							</button>
						)}
						{content}
					</div>
				) : (
					<button
						type="button"
						data-testid="speaker-bubble"
						aria-label={t("lesson.listen")}
						disabled={!asset}
						onClick={() => asset && sound.play(asset)}
						className={`relative flex min-h-16 w-full items-center gap-3 rounded-3xl px-4 py-3 ${FOCUS}`}
						style={surface}
					>
						{icon}
						{content}
					</button>
				)}
			</div>
		</div>
	);
}
